import type { WorkbenchSavePayload } from "@credit-acceptance/step6-bff-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { useSaveableSectionsStore } from "../../stores/saveableSectionsStore";
import { stipulationKeys } from "../stipulations/api/stipulationKeys";
import { dscWorkbenchKeys } from "./api/dscWorkbenchKeys";
import { useAdvancePreApproval } from "./api/useAdvancePreApproval";
import { useSaveWorkbench } from "./api/useSaveWorkbench";

/** Result of a saveAll attempt — `apiCallMade` tells callers whether the
 * outcome came back from the save API (vs. a pre-flight short-circuit like no
 * sections being registered), so they can decide whether it's appropriate to
 * surface as an API-response toast. */
export interface SaveAllResult {
  ok: boolean;
  apiCallMade: boolean;
  /**
   * HTTP status of the save response, when the API was actually called —
   * any 2xx lands here as `ok: true`, but callers that need to distinguish
   * an exact 200 (e.g. the footer's success toast) can check this directly.
   */
  statusCode?: number;
}

/**
 * Orchestrates the shared footer Save button and the Exit modal's Save & Exit:
 * runs every registered section's validation (so inline field errors still
 * surface), merges their payload contributions into one WorkbenchSavePayload,
 * and issues a single aggregate save call regardless of validation outcome —
 * the BFF, not the client, is the source of truth for whether a save
 * succeeds. `ok`/`apiCallMade` reflect the actual save API response, and each
 * section's own `onSaved`/`onSaveFailed` decides how it surfaces that result.
 * On any successful (2xx) save, awaits the Dashboard cache refresh, then —
 * only on an exact 200 for a pre-approval deal (mirrors the footer's success
 * toast gating) — the pre-approval-advance follow-up call
 * (useAdvancePreApproval), then the stipulations list refresh, in that order,
 * so the Exit modal's subsequent move-account call only fires once all three
 * have settled. The pre-approval-advance call stays best-effort: a failure
 * there is swallowed (logged by useAdvancePreApproval's own onError) and
 * never affects this save's own result or blocks the stipulations refresh.
 * `isSaving` stays true through this whole sequenced tail, not just the save
 * request itself, so the footer's Save button can't be re-clicked mid-chain.
 * @returns {{saveAll: (approvalId: string, isPreApprovalApp: boolean) => Promise<SaveAllResult>, isSaving: boolean}} The orchestrator.
 */
export function useSaveAllSections() {
  const saveWorkbench = useSaveWorkbench();
  const advancePreApproval = useAdvancePreApproval();
  const queryClient = useQueryClient();
  const [isSequencingFollowUps, setIsSequencingFollowUps] = useState(false);

  const saveAll = useCallback(
    async (approvalId: string, isPreApprovalApp: boolean): Promise<SaveAllResult> => {
      const entries = [...useSaveableSectionsStore.getState().sections.entries()];
      // Fail closed if nothing has registered yet (e.g. Save clicked before a
      // section mounts) rather than silently succeeding an empty-payload save.
      if (entries.length === 0) {
        return { ok: false, apiCallMade: false };
      }

      // Run validation for its side effect (surfacing inline errors); the
      // result no longer gates the save — a section stays invalid client-side
      // but still gets saved as-is, so a cleared/blank field always persists.
      await Promise.all(entries.map(([, section]) => section.validate()));

      const payload: WorkbenchSavePayload = Object.assign({}, ...entries.map(([, section]) => section.getPayload()));

      try {
        const { metadata, status } = await saveWorkbench.mutateAsync({ approvalId, payload });
        for (const [, section] of entries) {
          section.onSaved(metadata);
        }
        // Sequenced follow-ups: Dashboard refresh follows any successful save
        // (mutateAsync only resolves on 2xx), then — only on an exact 200 for
        // a pre-approval deal (mirrors the footer's success toast gating) —
        // the pre-approval-advance call, then the stipulations list refresh —
        // each awaited so the Exit modal's move-account call fires only after
        // all of them have settled.
        setIsSequencingFollowUps(true);
        try {
          await queryClient.invalidateQueries({ queryKey: dscWorkbenchKeys.detail({ approvalId }) });

          if (status === 200 && isPreApprovalApp) {
            try {
              await advancePreApproval.mutateAsync({ approvalId });
            } catch {
              // Best-effort: useAdvancePreApproval's onError already logs it;
              // never let this block the stipulations refresh or fail the save.
            }
          }

          await queryClient.invalidateQueries({ queryKey: stipulationKeys.lists() });
        } finally {
          setIsSequencingFollowUps(false);
        }
        return { ok: true, apiCallMade: true, statusCode: status };
      } catch (error) {
        for (const [, section] of entries) {
          section.onSaveFailed(error);
        }
        return { ok: false, apiCallMade: true };
      }
    },
    [advancePreApproval, queryClient, saveWorkbench]
  );

  return { saveAll, isSaving: saveWorkbench.isPending || isSequencingFollowUps };
}
